import { type FC, useEffect, useMemo, useState } from "react";
import { Button, DialogContent, Grid, Stack, Tooltip } from "@mui/material";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { BroadcastStateType } from "@/types/graphql/partner.generated";
import {
  audienceIsValid,
  marketingErrorKey,
  useArchiveAutomationsMutation,
  useAudienceEstimateQuery,
  useCancelBroadcastMutation,
  useMarketingComponents,
  useMarketingLimits,
  useSendBroadcastMutation,
  useUpdateBroadcastMutation,
  type AutomationDetails,
} from "@/partner/entities/marketing";
import { useUnsavedChangesGuard } from "@/partner/shared/hooks";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { DialogHeader, useConfirmAction } from "@/shared/components";
import { useDebounce } from "@/shared/hooks";
import {
  sendIssue,
  serializeForm,
  toBroadcastInput,
  type BroadcastFormState,
} from "../model/broadcastForm";
import { BroadcastAudienceCard } from "./BroadcastAudienceCard";
import { BroadcastMainCard } from "./BroadcastMainCard";
import { BroadcastScheduleCard } from "./BroadcastScheduleCard";
import { BroadcastStateChip } from "./BroadcastStateChip";
import { BroadcastStatsCard } from "./BroadcastStatsCard";
import { SendBroadcastConfirm } from "./SendBroadcastConfirm";

const ESTIMATE_DEBOUNCE_MS = 600;

interface BroadcastEditorProps {
  /** Живые данные рассылки: состояние и статистика обновляются опросом. */
  automation: AutomationDetails;
  initialState: BroadcastFormState;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Шапка и тело диалога рассылки: форма слева, превью письма или
 * результаты справа (как редактор письма EmailTemplateDialog). Править
 * можно только черновик; действия в шапке зависят от состояния.
 */
export const BroadcastEditor: FC<BroadcastEditorProps> = ({
  automation,
  initialState,
  onClose,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const { EmailTemplatePreview } = useMarketingComponents();
  const limits = useMarketingLimits();
  const [form, setForm] = useState(initialState);
  const [saved, setSaved] = useState(() => serializeForm(initialState));
  const dirty = useMemo(() => serializeForm(form) !== saved, [form, saved]);
  useUnsavedChangesGuard(dirty, t("shared.form.unsavedChangesWarning"));
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { id, state } = automation;
  const editable = state === BroadcastStateType.DraftType;
  const beforeSend = editable || state === BroadcastStateType.ScheduledType;
  const issue = editable ? sendIssue(form, moment(), limits) : null;
  const onChange = (patch: Partial<BroadcastFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // Оценка аудитории: через 600 мс после последней правки и только для
  // валидной аудитории, иначе бэкенд ответил бы ошибкой проверки.
  const estimateInput = useMemo(
    () => ({
      audience: form.viewersEnabled ? form.audience : null,
      contactListIds: form.contactListIds,
    }),
    [form.viewersEnabled, form.audience, form.contactListIds]
  );
  const debouncedEstimate = useDebounce(estimateInput, ESTIMATE_DEBOUNCE_MS);
  // Пока debounce не догнал последнюю правку, показанная оценка устарела:
  // запрещаем подтверждать отправку по ней (иначе после смены аудитории
  // можно за 600 мс успеть отправить по старому числу адресатов).
  const estimateStale = beforeSend && estimateInput !== debouncedEstimate;
  const skipEstimate =
    !beforeSend ||
    (debouncedEstimate.audience
      ? !audienceIsValid(debouncedEstimate.audience, limits)
      : debouncedEstimate.contactListIds.length === 0);
  const estimateQuery = useAudienceEstimateQuery({
    variables: debouncedEstimate,
    skip: skipEstimate,
    fetchPolicy: "cache-and-network",
  });
  const estimateData = skipEstimate ? null : estimateQuery.data;
  const estimateLoading = !skipEstimate && estimateQuery.loading;
  const estimate = estimateData
    ? estimateData.audienceEstimate.viewers +
      estimateData.audienceEstimate.contacts
    : null;

  const onError = (error: MutationErrorLike) =>
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
  const [updateBroadcast, { loading: updating }] = useUpdateBroadcastMutation({
    onError,
  });
  const [sendBroadcast, { loading: sending }] = useSendBroadcastMutation({
    onError,
  });
  const [cancelBroadcast, { loading: cancelling }] = useCancelBroadcastMutation(
    { onError }
  );
  const [archiveBroadcasts, { loading: archiving }] =
    useArchiveAutomationsMutation({ onError });
  const saving = updating || sending || cancelling || archiving;

  const save = async (): Promise<boolean> => {
    const snapshot = serializeForm(form);
    const result = await updateBroadcast({
      variables: { id, input: toBroadcastInput(form) },
    });
    if (!result.data) return false;
    setSaved(snapshot);
    return true;
  };

  const onSave = async () => {
    if (await save()) {
      enqueueSnackbar(t("broadcasts.edit.saved"), { variant: "success" });
    }
  };

  // Отправка идёт по сохранённой рассылке: несохранённые правки сначала
  // сохраняются, затем подтверждение с оценкой числа адресатов.
  const onSendClick = async () => {
    if (dirty && !(await save())) return;
    setConfirmOpen(true);
  };

  const scheduled = form.sendMode === "at";
  const onConfirmSend = async () => {
    const result = await sendBroadcast({
      variables: {
        id,
        scheduledAt: scheduled ? (form.scheduledAt?.toDate() ?? null) : null,
      },
    });
    setConfirmOpen(false);
    if (result.data) {
      enqueueSnackbar(
        scheduled
          ? t("broadcasts.send.scheduled")
          : t("broadcasts.send.success"),
        { variant: "success" }
      );
    }
  };

  const onCancelSend = async () => {
    const result = await cancelBroadcast({ variables: { id } });
    if (result.data) {
      enqueueSnackbar(t("broadcasts.send.cancelled"), { variant: "success" });
    }
  };

  const onArchive = () =>
    confirm({
      title: t("broadcasts.actions.archive"),
      text: t("broadcasts.edit.confirmArchive"),
      onConfirm: async () => {
        const result = await archiveBroadcasts({ variables: { ids: [id] } });
        if (result.data) {
          enqueueSnackbar(t("broadcasts.send.archived"), {
            variant: "success",
          });
        }
      },
    });

  const actions = [
    <Stack key="state" justifyContent="center">
      <BroadcastStateChip state={state} />
    </Stack>,
  ];
  if (editable) {
    actions.push(
      <Button
        key="save"
        variant="outlined"
        onClick={onSave}
        disabled={saving || !dirty || !form.name.trim()}
      >
        {t("broadcasts.actions.save")}
      </Button>,
      <Tooltip key="send" title={issue ? t(issue) : ""}>
        <span>
          <Button
            variant="contained"
            disableElevation
            onClick={onSendClick}
            disabled={
              saving || estimateLoading || estimateStale || Boolean(issue)
            }
          >
            {scheduled
              ? t("broadcasts.actions.schedule")
              : t("broadcasts.actions.send")}
          </Button>
        </span>
      </Tooltip>
    );
  }
  if (state === BroadcastStateType.ScheduledType) {
    actions.push(
      <Button
        key="cancel"
        variant="outlined"
        onClick={onCancelSend}
        disabled={saving}
      >
        {t("broadcasts.actions.cancel")}
      </Button>
    );
  }
  // В архив можно только черновик или уже отправленную рассылку: архивация
  // запланированной или отправляющейся останавливает доставку остальным
  // адресатам (BroadcastService.archiveAutomations переводит активные
  // enrollment-ы в EXITED), а кнопка выглядит как безобидная уборка списка.
  // Остановить отправку в процессе можно только явным «Отменить отправку»
  // для SCHEDULED (до старта); для SENDING отмены нет вовсе.
  if (
    state === BroadcastStateType.DraftType ||
    state === BroadcastStateType.SentType
  ) {
    actions.push(
      <Button
        key="archive"
        variant="outlined"
        onClick={onArchive}
        disabled={saving}
      >
        {t("broadcasts.actions.archive")}
      </Button>
    );
  }

  return (
    <>
      <DialogHeader
        title={form.name || t("broadcasts.edit.title")}
        loading={saving}
        onClose={onClose}
        actions={actions}
      />
      <DialogContent sx={{ p: 1 }}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={1}>
              <BroadcastMainCard
                form={form}
                onChange={onChange}
                disabled={!editable}
              />
              <BroadcastAudienceCard
                form={form}
                onChange={onChange}
                disabled={!editable}
                estimate={estimate}
              />
              <BroadcastScheduleCard
                form={form}
                onChange={onChange}
                disabled={!editable}
                startedAt={automation.startedAt ?? null}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }} sx={{ position: "sticky", top: 0 }}>
            {beforeSend ? (
              <EmailTemplatePreview emailTemplateId={form.emailTemplateId} />
            ) : (
              <BroadcastStatsCard automation={automation} />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      {confirmOpen && (
        <SendBroadcastConfirm
          count={estimate}
          scheduled={scheduled}
          loading={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onConfirmSend}
        />
      )}
      {confirmDialog}
    </>
  );
};
